import { getCanonicalCoursesLessonPath } from './legacy-course-url'

/**
 * HMI post-login / notification links use `?redirectUrl=` (not MFA `?redirect=`).
 * After login, membership-frontend often lands on `/courses?redirectUrl=...` and
 * never consumes that param, so the lesson (and nested `commentId`) never load.
 */

const readQueryParam = (value: unknown): string | undefined => {
  if (Array.isArray(value)) return value[0] ? String(value[0]) : undefined
  if (typeof value === 'string' && value.trim()) return String(value)
  return undefined
}

const isSafeCoursesRedirectPath = (path: string): boolean => {
  if (!path.startsWith('/') || path.startsWith('//') || path.includes('\\') || path.includes('://')) {
    return false
  }

  return path === '/courses' || path.startsWith('/courses/') || path.startsWith('/products/')
}

const normalizeCoursesRedirectPath = (path: string): string => {
  if (path.startsWith('/products/')) return `/courses${path}`
  return path
}

export interface CoursesQueryRedirectTarget {
  path: string
  query: Record<string, string>
  hash: string
}

/**
 * Resolves `query.redirectUrl` to a same-origin courses destination.
 * Legacy `categories/posts` paths are canonicalized here so the host does not
 * depend on a second middleware pass. Nested query (e.g. `commentId`) is
 * merged onto the destination; `redirectUrl` itself is dropped.
 */
export function resolveCoursesQueryRedirect(query: Record<string, unknown>): CoursesQueryRedirectTarget | null {
  const raw = readQueryParam(query.redirectUrl)
  if (!raw) return null

  try {
    const [pathAndSearch, hashFragment = ''] = raw.split('#')
    const searchStart = pathAndSearch.indexOf('?')
    const rawPath = searchStart === -1 ? pathAndSearch : pathAndSearch.slice(0, searchStart)
    const search = searchStart === -1 ? '' : pathAndSearch.slice(searchStart + 1)

    if (!isSafeCoursesRedirectPath(rawPath)) return null

    const normalizedPath = normalizeCoursesRedirectPath(rawPath)
    const path = getCanonicalCoursesLessonPath(normalizedPath) ?? normalizedPath

    const destinationQuery: Record<string, string> = {}
    for (const [key, value] of Object.entries(query)) {
      if (key === 'redirectUrl') continue
      const serialized = readQueryParam(value)
      if (serialized !== undefined) destinationQuery[key] = serialized
    }
    for (const [key, value] of new URLSearchParams(search).entries()) {
      destinationQuery[key] = value
    }

    return {
      path,
      query: destinationQuery,
      hash: hashFragment ? `#${hashFragment}` : '',
    }
  } catch {
    return null
  }
}

export function isSameCoursesRedirectTarget(
  current: { path: string; query: Record<string, unknown>; hash?: string },
  target: CoursesQueryRedirectTarget
): boolean {
  if (readQueryParam(current.query.redirectUrl)) return false
  if (current.path !== target.path) return false
  if ((current.hash || '') !== target.hash) return false

  const currentKeys = Object.keys(current.query)
  const targetKeys = Object.keys(target.query)
  if (currentKeys.length !== targetKeys.length) return false

  return currentKeys.every(key => readQueryParam(current.query[key]) === target.query[key])
}
